import moment from 'moment'
import { createClaim, getClaimWizardPackage, getProductsAndRisksRequest, getQualificationsRequest, getDocumentationRequest, updateClaimContact, setClaimWizardStep } from '../../state/actions';
import i18n from 'lib/i18n'

const _ = i18n('claimWizard')

// ================================
// #region Form Fields
// ================================

export const stepOneFormFields = [
    'stepOne.selfManaging', // only show if user is allowed to mandate tis is determined in office, if is not allowed default is false 
    'stepOne.brokerName', // not editable autofill from office, consultation only
    'stepOne.claimNumber', // first touch is empty (claim has no yet been created) autofill, not editable
    'stepOne.clientName', // autofill from client consultation only 
    'stepOne.mobileNumber', // both phoneNumber and mobileNumber fields are automatically filled in with default data from getClient 
    'stepOne.phoneNumber', // need to create 2 list of phones numbers one for gsm and one for landline 
    'stepOne.email', //auto fill with default from getClient also user could type in a different email
    'stepOne.accountNumber', // bank account auto fill from list of bank accounts from getClient also user could type in a different IBAN (no default)
    'stepOne.accountConfirmation', // boolean not sent to API, user needs to confirm bank account
    'stepOne.claimDate', // mandatory date picker field
    'stepOne.claimRefBroker', // Input filed where user can add his own reference not mandatory
    'stepOne.productId', // List of products from getProductsAndRisks call
    'stepOne.riskId', // List of risk from the product selected from getProductsAndRisks call
    'stepOne.riskDescription', // Translations based on riskId - used for claim list description
    'stepOne.qualificationNumber', // List of qualifications from the getQualification call based on selected product branch
    'stepOne.qualificationLabel', // Translations based on qualificationNumber - used for claim list description
    'stepOne.subQualificationNumber', // List of subQualifications from the selected qualification 
    'stepOne.contractIdHist',
    'stepFour.claimComment' // used in step 4 to create or update claim comment
]

// #endregion

// ================================
// #region Form Validations
// ================================

export const disableRiskAndQualificationFields = (productField) => {
    let isDisabled = true
    if (productField && productField.value) {
        isDisabled = false
    }
    return isDisabled
}

export const validate = (values, props) => {
    const { hasSubQualifications } = props

    const errors = { stepOne: {} }

    // Unified validation for email, mobileNumber, and phoneNumber
    const hasContactInfo = values.stepOne.email || values.stepOne.mobileNumber || values.stepOne.phoneNumber

    if (!hasContactInfo) {
        errors.stepOne.email = _('requiredContactInfo')
    } else {
        if (values.stepOne.mobileNumber && !/^\d+$/.test(values.stepOne.mobileNumber)) {
            errors.stepOne.mobileNumber = _('invalidMobileNumber')
        }
        if (values.stepOne.phoneNumber && !/^\d+$/.test(values.stepOne.phoneNumber)) {
            errors.stepOne.phoneNumber = _('invalidPhoneNumber')
        }
        if (values.stepOne.mobileNumber && values.stepOne.mobileNumber.length > 20) {
            errors.stepOne.mobileNumber = _('mobileNumberMaxLength20')
        }
        if (values.stepOne.phoneNumber && values.stepOne.phoneNumber.length > 20) {
            errors.stepOne.phoneNumber = _('phoneNumberMaxLength20')
        }
        if (values.stepOne.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.stepOne.email)) {
            errors.stepOne.email = _('invalidEmail')
        }
    }

    if (values.stepOne.accountNumber) {
        if (values.stepOne.accountNumber.length > 20) {
            errors.stepOne.accountNumber = _('invalidAccountNumber')
        }
        if (!values.stepOne.accountConfirmation) {
            errors.stepOne.accountConfirmation = _('requiredAccountConfirmation')
        }
    }

    if (!values.stepOne.claimDate) {
        errors.stepOne.claimDate = _('requiredClaimDate')
    }

    if (!values.stepOne.productId) {
        errors.stepOne.productId = _('requiredProduct')
    }

    if (!values.stepOne.riskId) {
        errors.stepOne.riskId = _('requiredRisk')
    }

    if (!values.stepOne.qualificationNumber) {
        errors.stepOne.qualificationNumber = _('requiredQualification')
    }

    if (hasSubQualifications && !values.stepOne.subQualificationNumber) {
        errors.stepOne.subQualificationNumber = _('requiredSubQualification')
    }

    return errors
}


/**
     * check if there are missing documents and also handles case when there are alternative documents selected
     * @returns array of missing documents
     */
export const missingWizardDocuments = (missingInfo, oraDocuments, docsFromDocumentation, documentFiles) => {
    const missingDocuments = []
    if (missingInfo && missingInfo.documents && missingInfo.documents.length > 0) {
        for (const oneDoc of missingInfo.documents) {
            if (oraDocuments && oraDocuments.length > 0 && oneDoc.required === 'Wizard') {
                const missingDocumentData = oraDocuments.find(doc => doc.docType === oneDoc.docType)
                if (missingDocumentData && missingDocumentData.alternativeDocTypes && missingDocumentData.alternativeDocTypes.length > 0) {
                    //loop through alternative documents and if one alternative has altDocCount > 0, then it is selected
                    const selectedAlternative = missingDocumentData.alternativeDocTypes.find(altDoc => +altDoc.altDocCount > 0)
                    if (selectedAlternative) {
                        missingDocuments.push(...missingDocumentData.alternativeDocTypes
                            .filter(altDoc => +altDoc.altDocCount === 0 && altDoc.alternativeNr === selectedAlternative.alternativeNr)
                            .map(altDoc => altDoc.altDocType) || [])
                    } else {
                        missingDocuments.push(oneDoc.docType)
                    }
                } else if (missingDocumentData && missingDocumentData.docCount && +missingDocumentData.docCount <= 0) {
                    missingDocuments.push(oneDoc.docType)
                }
            }
        }
        const finalResults = []
        //Check if there are documents uploaded that are pending approval from oracle 
        missingDocuments.forEach(oneMissingDoc => {
            const relevantDocumentFiles = documentFiles.filter(oneFile => oneFile.uploaded && oneFile.docType === oneMissingDoc || oneFile.mainDocType)
            if (!relevantDocumentFiles) {
                finalResults.push(file.docType)
            } else {
                //at this point the missing document has a upload or a alternative has a upload
                for (const file of relevantDocumentFiles) {
                    // check if file is an alternative, if it is we must check if there are alternatives without upload
                    if (file.mainDocType && file.alternativeId) {
                        const documentFromDocumentation = docsFromDocumentation.find(oneDoc => oneDoc.docType === file.mainDocType)
                        if (documentFromDocumentation) {
                            const alternativeType = documentFromDocumentation.alternativeDocTypes.find(oneAltType => oneAltType.alternativeNr === file.alternativeId)
                            if (alternativeType) {
                                for (const oneAlt of alternativeType.alternatives) {
                                    if (!documentFiles.some(oneFile => oneFile.uploaded && oneFile.docType === oneAlt)) {
                                        finalResults.push(oneAlt)
                                    }
                                }
                            }
                        }
                    }
                }
            }

        })

        return [...new Set(finalResults)]
    }
}


// #endregion

// ================================
// #region Form Handlers
// ================================

export const updatedFormValue = (value, field, dispatch) => {
    dispatch({
        type: "redux-form/CHANGE",
        field: field.name,
        value: value && value.value || '',
        touch: value && value.value ? !!value.value : false,
        form: "claimWizardForm"
    })
}

export const setInitialValues = (dispatch, existingClaimPackage, fields, {
    brokerName, clientData, mobilePhoneOptions, phoneNumberOptions, emailOptions, claimComment
}) => {
    Object.keys(fields.stepOne).forEach(key => {
        if (existingClaimPackage && existingClaimPackage[key]) {
            updatedFormValue({ value: existingClaimPackage[key], touched: true }, fields.stepOne[key], dispatch)
        } else {
            switch (key) {
                case 'selfManaging':
                    updatedFormValue({ value: 'false', touched: true }, fields.stepOne[key], dispatch)
                    break;
                case 'brokerName':
                    updatedFormValue({ value: brokerName, touched: true }, fields.stepOne[key], dispatch)
                    break;
                case 'clientName':
                    updatedFormValue({ value: clientData.main.name, touched: true }, fields.stepOne[key], dispatch)
                    break;
                case 'claimDate':
                    updatedFormValue({ value: existingClaimPackage && Object.keys(existingClaimPackage).length > 0 ? moment().format('YYYY-MM-DD') : "", touched: true }, fields.stepOne[key], dispatch)
                    break;
                case 'mobileNumber': {
                    const defaultPhoneNumber = mobilePhoneOptions.find(oneNumber => oneNumber.default)
                    updatedFormValue(
                        {
                            value: defaultPhoneNumber ? defaultPhoneNumber.value : '',
                            touched: false
                        },
                        fields.stepOne[key],
                        dispatch
                    )
                    break
                }
                case 'phoneNumber': {
                    const defaultPhoneNumber = phoneNumberOptions.find(oneNumber => oneNumber.default)
                    updatedFormValue(
                        {
                            value: defaultPhoneNumber ? defaultPhoneNumber.value : '',
                            touched: false
                        },
                        fields.stepOne[key],
                        dispatch
                    )
                    break
                }
                case 'email': {
                    const defaultEmail = emailOptions.find(oneNumber => oneNumber.default)
                    updatedFormValue(
                        {
                            value: defaultEmail ? defaultEmail.value : '',
                            touched: false
                        },
                        fields.stepOne[key],
                        dispatch
                    )
                    break
                }
                default:
                    break
            }
        }
    })
    if (claimComment) {
        updatedFormValue({ value: claimComment, touched: true }, fields.stepFour.claimComment, dispatch)
    }
}

export const updateFormFieldsAfterClaimUpdate = (dispatch, fields, claimData) => {
    Object.keys(claimData).forEach(key => {
        if (fields.stepOne[key]) {
            updatedFormValue({ value: claimData[key], touched: true }, fields.stepOne[key], dispatch)
        }
    })
}

export const resetProductAndRisk = (fields, dispatch) => {
    updatedFormValue({ value: '', touched: true }, fields.stepOne.productId, dispatch)
    updatedFormValue({ value: '', touched: true }, fields.stepOne.riskId, dispatch)
    updatedFormValue({ value: '', touched: true }, fields.stepOne.riskDescription, dispatch)
    updatedFormValue({ value: '', touched: true }, fields.stepOne.qualificationNumber, dispatch)
    updatedFormValue({ value: '', touched: true }, fields.stepOne.subQualificationNumber, dispatch)
}

export const prepareDataForClaim = (formData) => {
    const claimArgs = {}
    Object.keys(formData).forEach(key => {
        claimArgs[key] = formData[key].value
    })
    return claimArgs
}

export const prepareDataForUpdateContact = (claimData, fields) => {
    const updateArgs = {
        mongoRecordId: claimData._id,
        claimId: claimData.claimId,
        clientId: claimData.clientId,
        mobileNumber: fields.mobileNumber.value,
        phoneNumber: fields.phoneNumber.value,
        email: fields.email.value,
        accountNumber: fields.accountNumber.value,
        claimRefBroker: fields.claimRefBroker.value,
        accountConfirmation: fields.accountConfirmation.value
    }
    return updateArgs
}

export const generateInvolvedPartiesData = (involvedPartiesData) => {
    const involvedParties = []
    for (const oneInvParty of involvedPartiesData) {
        const onePartyData = {
            partyNumber: oneInvParty.partyNumber
        }
        Object.keys(oneInvParty.fields).forEach(key => {
            onePartyData[key] = oneInvParty.fields[key].value
        })
        involvedParties.push(onePartyData)
    }
    return involvedParties
}

// #endregion

// ================================
// #region List Options 
// ================================

export const setMobilePhoneOptions = (phoneNumbers) => {
    const result = []

    phoneNumbers.forEach(oneNumber => {
        if ((typeof oneNumber.mobile === 'boolean' && oneNumber.mobile) || oneNumber.mobile === 'true') {
            const oneOption = {
                label: oneNumber.number,
                value: oneNumber.number,
            }
            if ((typeof oneNumber.default === 'boolean' && oneNumber.default) || oneNumber.default === 'true') {
                oneOption.default = true
            }
            result.push(oneOption)
        }
    })
    // fix for when there are no mobile numbers and list is empty and the selector fails to input a custom value
    if (result.length === 0) {
        result.push({ label: '', value: '' })
    }
    return result
}

export const setPhoneNumbersOptions = (phoneNumbers) => {
    const result = []
    phoneNumbers.forEach(oneNumber => {
        if ((typeof oneNumber.mobile === 'boolean' && !oneNumber.mobile) || oneNumber.mobile === 'false') {
            const oneOption = {
                label: oneNumber.number,
                value: oneNumber.number,
            }
            if ((typeof oneNumber.default === 'boolean' && oneNumber.default) || oneNumber.default === 'true') {
                oneOption.default = true
            }
            result.push(oneOption)
        }
    })
    // fix for when there are no mobile numbers and list is empty and the selector fails to input a custom value
    if (result.length === 0) {
        result.push({ label: '', value: '' })
    }
    return result
}

export const setEmailOptions = (emailsList) => {
    const result = emailsList.map(emailOption => ({
        label: emailOption.email, value: emailOption.email,
        default: (typeof emailOption.default === 'boolean' ? emailOption.default : emailOption.default === 'true' ? true : false)
    }))

    if (result.length === 0) {
        result.push({ label: '', value: '' })
    }
    return result
}

export const setAccountOptions = (accountNumbers) => {
    return accountNumbers.map(oneAccount => ({ label: oneAccount.IBAN, value: oneAccount.IBAN }))
}

export const setRiskAndQualificationsOptions = async (dispatch, productId, productList, selfManaging, contractId, contractIdHist) => {
    let riskOptionsList = []
    let qualificationOptionsList = []
    let qualificationList = []
    const product = productList.find(oneProduct => oneProduct.productId === productId)
    if (product) {
        const branch = product.branch
        const qualificationsRes = await getQualifications(dispatch, branch, contractId, contractIdHist)
        if (qualificationsRes) {
            qualificationList = qualificationsRes.qualifications
        }
        if (selfManaging === 'true') {
            qualificationList = qualificationList.filter(qualification => qualification.legalAid === 'false')
        }
        qualificationOptionsList = qualificationList.map(oneQualification => {
            return {
                value: oneQualification.qualificationNumber,
                label: oneQualification.qualificationName
            }
        })
        // set the risk options
        riskOptionsList = product.risks.map(oneRisk => ({ value: oneRisk.riskId, label: oneRisk.riskDescription }))
        //reset selected 
    }
    riskOptionsList.sort((a, b) => a.label.localeCompare(b.label))
    qualificationOptionsList.sort((a, b) => a.label.localeCompare(b.label))
    return { riskOptionsList, qualificationList, qualificationOptionsList }
}


// #endregion

// ================================
// #region API requests
// ================================

export const getAndSetProductsAndRisks = async (dispatch, contractId, claimDate, fields) => {
    try {
        const result = await dispatch(getProductsAndRisksRequest(contractId, claimDate))
        if (result && result.products) {
            const productsList = result && result.products || []
            const productListOptions = []
            const contractIdHist = result && result.contractIdHist || ''
            for (const oneProduct of productsList) {
                productListOptions.push({
                    label: `${oneProduct.product} - ${oneProduct.option}`,
                    value: oneProduct.productId
                })
            }
            productListOptions.sort((a, b) => a.label.localeCompare(b.label))
            //update to new contractIdHist
            updatedFormValue({ value: contractIdHist, touched: true }, fields.stepOne.contractIdHist, dispatch)

            return { productsList, productListOptions }
        }

        return { productsList: [], productListOptions: [] }
    } catch (error) {
        console.error('getAndSetProductsAndRisks error: ', error)
        return { productsList: [], productListOptions: [] }
    }
}

export const getQualifications = async (dispatch, branch, contractId, contractIdHist) => {
    try {
        const result = dispatch(getQualificationsRequest(branch, contractId, contractIdHist))
        if (result) {
            return result
        }
    } catch (error) {
        console.warn('Error getting qualifications: ', error);
    }

}

export const saveNewClaim = async (dispatch, claimArgs) => {
    try {
        await dispatch(createClaim(claimArgs))
        await dispatch(getClaimWizardPackage({ contractId: claimArgs.contractId }))
        await dispatch(getDocumentationRequest(claimArgs.qualificationNumber, claimArgs.subQualificationNumber))
        await dispatch(setClaimWizardStep('2'))

    } catch (error) {
        console.warn(error);
    }
}

export const updateClaimContactAndSetStep2 = async (dispatch, claimData, fields) => {
    const updateArguments = prepareDataForUpdateContact(claimData, fields.stepOne)
    updateArguments.brokerNumber = claimData.brokerNumber
    await dispatch(updateClaimContact(updateArguments))
    await dispatch(getClaimWizardPackage({ contractId: claimData.contractId }))
    await dispatch(getDocumentationRequest(claimData.qualificationNumber, claimData.subQualificationNumber))
    await dispatch(setClaimWizardStep('2'))
}

export const getClaimMongoRecord = async (dispatch, args) => {
    try {
        const claimMongoRecord = await dispatch(getClaimWizardPackage(args))
        if (claimMongoRecord) {
            return claimMongoRecord
        } else {
            console.warn("No claimMongoRecord found:", args)
            return null
        }
    } catch (error) {
        console.error("Error in getClaimMongoRecord:", error)
        return null
    }
}

export const openOverviewPdf = async (claimPackageId) => {
    const overviewPDFLink = `api/getOverviewPdfForPrint/${claimPackageId}`
    const w = window.open(overviewPDFLink, '_blank')

    const onLoadWindowPromise = new Promise((resolve) => {
        w.addEventListener('load', () => {
            resolve()
        }, true)
    })

    await onLoadWindowPromise
}

// #endregion



// WEBPACK FOOTER //
// ./components/ClaimsWizard/claimsWizardService.js